import React from 'react';
import { PlaceSearchInput, CITY_TYPES } from '../../../components/ui/PlaceSearchInput';
import {
  GROUND_NEXT_UP,
  PLAN_NAME_MAX_LENGTH,
  PLAN_NAME_PLACEHOLDER,
  buildGroundPlaceFromSelection,
  buildGroundReadout,
  bumpGroundAcres,
  canShowGroundClubSource,
  groundAcresHint,
  groundMapPullLabel,
  groundPlotsEmptyCopy,
} from './plantingGroundUtils';
import {
  daysInFrostMonth,
  formatFrostMonthDay,
  frostMonthOptions,
  isManualGroundSource,
  parseFrostMonthDay,
} from './plantingGroundOverrideUtils';
import { formatPlotAcreageBadge, getPlotScopeLabel } from './plantingAssistantUtils';

function plotNote(poly, mode, selectedClubName) {
  const scope = getPlotScopeLabel(poly, mode, selectedClubName);
  const location = String(poly?.location || '').trim();
  if (location) return location.toUpperCase();
  return String(scope || 'PLOT').toUpperCase();
}

function formatZoneOptionLabel(zone) {
  if (!zone) return '';
  const code = String(zone.value || zone.id || '').toUpperCase();
  const label = String(zone.label || '').trim();
  if (label && label.toUpperCase() !== code) return `${code} — ${label}`;
  return code;
}

function FrostMonthDaySelects({
  value,
  onChange,
  ariaLabel,
  testId,
}) {
  const parsed = parseFrostMonthDay(value);
  const month = parsed.month || '';
  const day = parsed.day || '';
  const maxDay = daysInFrostMonth(month || 1);
  const months = frostMonthOptions();

  const emit = (nextMonth, nextDay) => {
    const monthNum = Number(nextMonth);
    const dayNum = Number(nextDay);
    if (!monthNum) {
      onChange('');
      return;
    }
    const clamped = Math.min(Math.max(dayNum || 1, 1), daysInFrostMonth(monthNum));
    onChange(formatFrostMonthDay(monthNum, clamped));
  };

  return (
    <div className="plant-ground-tile__frost-selects">
      <select
        className="plant-ground-tile__control"
        aria-label={`${ariaLabel} month`}
        value={month}
        onChange={(event) => emit(event.target.value, day || 1)}
        data-testid={`${testId}-month`}
      >
        <option value="">Month</option>
        {months.map((item) => (
          <option key={item.value} value={item.value}>{item.label}</option>
        ))}
      </select>
      <select
        className="plant-ground-tile__control"
        aria-label={`${ariaLabel} day`}
        value={day}
        onChange={(event) => emit(month || 1, event.target.value)}
        data-testid={`${testId}-day`}
      >
        <option value="">Day</option>
        {Array.from({ length: maxDay }, (_, index) => index + 1).map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </div>
  );
}

function ReadoutTile({
  tile,
  overrideMode,
  editor = null,
}) {
  const editable = overrideMode && editor;
  return (
    <div
      className={[
        'plant-ground-tile',
        `plant-ground-tile--${tile.size}`,
        `plant-ground-tile--${tile.tone}`,
        editable ? 'is-override' : '',
        editor?.manual ? 'is-manual' : '',
        tile.label === 'FROST WINDOW' ? 'plant-ground-tile--frost' : '',
      ].filter(Boolean).join(' ')}
      data-testid={`plant-ground-tile-${tile.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="plant-ground-tile__label">
        {tile.label}
        {editor?.manual ? <span className="plant-ground-tile__badge">YOU</span> : null}
      </div>
      {editable ? editor.control : (
        <div className="plant-ground-tile__value">{tile.value}</div>
      )}
    </div>
  );
}

export function PlantingGroundStep({
  acres,
  onAcresChange,
  placeQuery = '',
  onPlaceQueryChange,
  selectedPlace = null,
  onPlaceSelect,
  plots = [],
  selectedIds,
  onTogglePlot,
  mode = 'personal',
  selectedClubName = '',
  presidentClubs = [],
  selectedClubId = null,
  clubsLoading = false,
  onModeChange,
  onClubSelect,
  plotsLoading = false,
  plotsError = null,
  draftZoneCode = '',
  draftZoneSource = '',
  draftSoilType = '',
  draftSoilTypeLabel = '',
  draftSoilTypeId = '',
  draftSoilTypeSource = '',
  draftSoilDrainage = '',
  draftSoilDrainageLabel = '',
  draftSoilDrainageSource = '',
  draftSoilPh = '',
  draftSoilPhSource = '',
  draftSoilSeries = '',
  draftSoilDrainageRaw = '',
  draftFrostLastLabel = '',
  draftFrostFirstLabel = '',
  draftFrostSource = '',
  soilSurveyCovered = false,
  soilLookupLoading = false,
  selectedPlotCount = 0,
  zoneOptionsList = [],
  soilTypeOptions = [],
  soilDrainageOptions = [],
  onZoneOverride,
  onSoilTypeOverride,
  onSoilDrainageOverride,
  onSoilPhOverride,
  onFrostOverride,
  onResetToSurvey,
  planName = '',
  onPlanNameChange,
  nameNeeded = false,
  readOnly = false,
}) {
  const [overrideMode, setOverrideMode] = React.useState(false);
  const nameInputRef = React.useRef(null);
  const canEdit = !readOnly;
  React.useEffect(() => {
    if (!nameNeeded || !canEdit) return;
    nameInputRef.current?.focus();
  }, [nameNeeded, canEdit]);
  const showOverrideEditors = overrideMode && canEdit;
  const showClubSource = !clubsLoading && canShowGroundClubSource(presidentClubs);
  const showClubPicker = showClubSource && mode === 'club' && presidentClubs.length > 1;
  const selectedCount = selectedIds instanceof Set ? selectedIds.size : selectedPlotCount;
  const place = buildGroundPlaceFromSelection(selectedPlace);
  const readout = buildGroundReadout({
    acresValue: acres,
    place,
    selectedPlotCount: selectedCount,
    draftZoneCode,
    draftSoilTypeLabel,
    draftSoilTypeId,
    draftSoilDrainageLabel,
    draftSoilPh,
    draftSoilSeries,
    draftSoilDrainageRaw,
    draftFrostLastLabel,
    draftFrostFirstLabel,
    soilSurveyCovered,
    soilLookupLoading,
  });
  const canOverride = readout.readState === 'RESOLVED' || selectedCount > 0 || !!place;

  const editors = {
    'USDA ZONE': {
      manual: isManualGroundSource(draftZoneSource),
      control: (
        <select
          className="plant-ground-tile__control"
          aria-label="Override USDA zone"
          value={draftZoneCode || ''}
          onChange={(event) => onZoneOverride?.(event.target.value)}
          data-testid="plant-ground-override-zone"
        >
          <option value="">Select zone</option>
          {(zoneOptionsList || []).map((zone) => (
            <option key={zone.value} value={zone.value}>
              {formatZoneOptionLabel(zone)}
            </option>
          ))}
        </select>
      ),
    },
    SOIL: {
      manual: isManualGroundSource(draftSoilTypeSource),
      control: (
        <select
          className="plant-ground-tile__control"
          aria-label="Override soil type"
          value={draftSoilType || draftSoilTypeId || ''}
          onChange={(event) => onSoilTypeOverride?.(event.target.value)}
          data-testid="plant-ground-override-soil"
        >
          <option value="">Not sure / mixed</option>
          {(soilTypeOptions || []).map((type) => (
            <option key={type.id} value={type.id}>
              {type.label}
            </option>
          ))}
        </select>
      ),
    },
    DRAINAGE: {
      manual: isManualGroundSource(draftSoilDrainageSource),
      control: (
        <select
          className="plant-ground-tile__control"
          aria-label="Override drainage"
          value={draftSoilDrainage || ''}
          onChange={(event) => onSoilDrainageOverride?.(event.target.value)}
          data-testid="plant-ground-override-drainage"
        >
          <option value="">Select drainage</option>
          {(soilDrainageOptions || []).map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    pH: {
      manual: isManualGroundSource(draftSoilPhSource),
      control: (
        <input
          className="plant-ground-tile__control plant-ground-tile__control--input"
          type="number"
          inputMode="decimal"
          step="0.1"
          min="0"
          max="14"
          aria-label="Override soil pH"
          value={draftSoilPh}
          placeholder="6.5"
          onChange={(event) => onSoilPhOverride?.(event.target.value)}
          data-testid="plant-ground-override-ph"
        />
      ),
    },
    'FROST WINDOW': {
      manual: isManualGroundSource(draftFrostSource),
      control: (
        <div className="plant-ground-tile__frost">
          <div className="plant-ground-tile__frost-pair">
            <span className="plant-ground-tile__frost-kicker">LAST</span>
            <FrostMonthDaySelects
              value={draftFrostLastLabel}
              onChange={(next) => onFrostOverride?.(next, draftFrostFirstLabel)}
              ariaLabel="Override last spring frost"
              testId="plant-ground-override-frost-last"
            />
          </div>
          <span className="plant-ground-tile__frost-sep" aria-hidden="true">–</span>
          <div className="plant-ground-tile__frost-pair">
            <span className="plant-ground-tile__frost-kicker">FIRST</span>
            <FrostMonthDaySelects
              value={draftFrostFirstLabel}
              onChange={(next) => onFrostOverride?.(draftFrostLastLabel, next)}
              ariaLabel="Override first fall frost"
              testId="plant-ground-override-frost-first"
            />
          </div>
        </div>
      ),
    },
  };

  return (
    <section className="plant-ground" data-testid="plant-ground-step">
      <div className="plant-ground__grid">
        <div className="plant-ground-card">
          <div className="plant-ground-card__head">TELL ME ABOUT THE GROUND</div>
          <div className="plant-ground-card__body">
            <div className={`plant-ground-field${readOnly ? ' is-readonly' : ''}${nameNeeded && canEdit ? ' is-needed' : ''}`}>
              <label className="plant-ground-field__label" htmlFor="plant-ground-name">
                What do you call this plan?
              </label>
              <input
                ref={nameInputRef}
                id="plant-ground-name"
                className="plant-ground-name"
                type="text"
                value={planName}
                placeholder={PLAN_NAME_PLACEHOLDER}
                maxLength={PLAN_NAME_MAX_LENGTH}
                disabled={!canEdit}
                aria-invalid={nameNeeded && canEdit ? true : undefined}
                aria-describedby="plant-ground-name-hint"
                onChange={(event) => onPlanNameChange?.(event.target.value)}
                data-testid="plant-ground-name"
              />
              <p className="plant-ground-field__hint" id="plant-ground-name-hint" data-testid="plant-ground-name-hint">
                {nameNeeded && canEdit
                  ? 'Give it a name to continue — you’ll see it on My Plans and the co-op PDF.'
                  : 'You’ll see this name on My Plans and on the PDF you take to the co-op.'}
              </p>
            </div>

            <div className={`plant-ground-field${showOverrideEditors ? ' is-override' : ''}${readOnly ? ' is-readonly' : ''}`}>
              <label className="plant-ground-field__label" htmlFor="plant-ground-acres">
                How many acres are you planting?
              </label>
              <div className="plant-ground-acres">
                <button
                  type="button"
                  className="plant-ground-acres__step"
                  aria-label="Decrease acres"
                  disabled={!canEdit}
                  onClick={() => onAcresChange(bumpGroundAcres(acres, -0.5))}
                >
                  −
                </button>
                <input
                  id="plant-ground-acres"
                  className="plant-ground-acres__input"
                  type="text"
                  inputMode="decimal"
                  value={acres}
                  placeholder="0.0"
                  disabled={!canEdit}
                  onChange={(event) => onAcresChange(event.target.value)}
                  data-testid="plant-ground-acres"
                />
                <button
                  type="button"
                  className="plant-ground-acres__step"
                  aria-label="Increase acres"
                  disabled={!canEdit}
                  onClick={() => onAcresChange(bumpGroundAcres(acres, 0.5))}
                >
                  +
                </button>
              </div>
              <p className="plant-ground-field__hint">{groundAcresHint(selectedCount)}</p>
            </div>

            <div className={`plant-ground-field${readOnly ? ' is-readonly' : ''}`}>
              <label className="plant-ground-field__label" htmlFor="plant-ground-town">
                Closest town
              </label>
              <PlaceSearchInput
                inputId="plant-ground-town"
                className="plant-ground-place-search"
                value={placeQuery}
                onChange={canEdit ? onPlaceQueryChange : undefined}
                onSelect={canEdit ? onPlaceSelect : undefined}
                types={CITY_TYPES}
                limit={6}
                placeholder="Search city or town"
                aria-label="Search closest town"
                data-testid="plant-ground-town"
              />
              <p className="plant-ground-field__hint">
                The town sets your hardiness zone and pulls the soil survey. No map drawing required.
              </p>
            </div>

            <div className="plant-ground-or" role="separator">
              <span className="plant-ground-or__rule" />
              <span className="plant-ground-or__label">{groundMapPullLabel(mode)}</span>
              <span className="plant-ground-or__rule" />
            </div>

            {showClubSource ? (
              <div className="plant-ground-source" data-testid="plant-ground-map-source">
                <div className="plant-ground-source__seg" role="group" aria-label="Map source">
                  <button
                    type="button"
                    className={`plant-ground-source__btn${mode === 'personal' ? ' is-active' : ''}`}
                    aria-pressed={mode === 'personal'}
                    disabled={!canEdit}
                    onClick={() => onModeChange?.('personal')}
                    data-testid="plant-ground-source-personal"
                  >
                    Personal
                  </button>
                  <button
                    type="button"
                    className={`plant-ground-source__btn${mode === 'club' ? ' is-active' : ''}`}
                    aria-pressed={mode === 'club'}
                    disabled={!canEdit}
                    onClick={() => onModeChange?.('club')}
                    data-testid="plant-ground-source-club"
                  >
                    Club
                  </button>
                </div>
                {showClubPicker ? (
                  <select
                    className="plant-ground-source__club"
                    aria-label="Select club"
                    value={selectedClubId || ''}
                    disabled={!canEdit}
                    onChange={(event) => onClubSelect?.(event.target.value)}
                    data-testid="plant-ground-source-club-select"
                  >
                    {presidentClubs.map((club) => (
                      <option key={club.id} value={club.id}>{club.name}</option>
                    ))}
                  </select>
                ) : null}
              </div>
            ) : null}

            <div className="plant-ground-plots" data-testid="plant-ground-plots">
              {plotsLoading ? (
                <p className="plant-ground-plots__empty">Loading plots…</p>
              ) : null}
              {plotsError ? (
                <p className="plant-ground-plots__empty plant-ground-plots__empty--error">{plotsError}</p>
              ) : null}
              {!plotsLoading && !plotsError && plots.length === 0 ? (
                <p className="plant-ground-plots__empty">
                  {groundPlotsEmptyCopy(mode)}
                </p>
              ) : null}
              {!plotsLoading && !plotsError
                ? plots.map((poly) => {
                  const on = selectedIds instanceof Set && selectedIds.has(poly.id);
                  const acresLabel = formatPlotAcreageBadge(poly.acreage);
                  return (
                    <button
                      key={poly.id}
                      type="button"
                      className={`plant-ground-plot${on ? ' is-selected' : ''}`}
                      onClick={() => canEdit && onTogglePlot(poly.id)}
                      aria-pressed={on}
                      disabled={!canEdit}
                      data-testid={`plant-ground-plot-${poly.id}`}
                    >
                      <span className={`plant-ground-plot__tick${on ? ' is-on' : ''}`} aria-hidden="true">
                        {on ? '✓' : ''}
                      </span>
                      <span className="plant-ground-plot__body">
                        <span className="plant-ground-plot__name">{poly.name}</span>
                        <span className="plant-ground-plot__note">
                          {plotNote(poly, mode, selectedClubName)}
                        </span>
                      </span>
                      <span className="plant-ground-plot__ac">{acresLabel}</span>
                    </button>
                  );
                })
                : null}
            </div>
          </div>
        </div>

        <div className="plant-ground-side">
          <div className={`plant-ground-read${showOverrideEditors ? ' is-override' : ''}`}>
            <div className="plant-ground-read__head">
              <span>{showOverrideEditors ? 'YOUR OVERRIDES' : 'WHAT WE READ'}</span>
              <span className="plant-ground-read__state">
                {readOnly ? 'VIEWING' : showOverrideEditors ? 'EDITING' : readout.readState}
              </span>
            </div>
            <div className="plant-ground-read__grid">
              {readout.tiles.map((tile) => (
                <ReadoutTile
                  key={tile.label}
                  tile={tile}
                  overrideMode={showOverrideEditors && tile.label !== 'ACRES'}
                  editor={editors[tile.label] || null}
                />
              ))}
            </div>
            <div className="plant-ground-read__source">{readout.sourceLine}</div>
            {canEdit ? (
            <div className="plant-ground-read__actions">
              {!overrideMode ? (
                <button
                  type="button"
                  className="plant-ground-override-btn"
                  disabled={!canOverride}
                  onClick={() => setOverrideMode(true)}
                  data-testid="plant-ground-override"
                >
                  OVERRIDE
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="plant-ground-override-btn plant-ground-override-btn--done"
                    onClick={() => setOverrideMode(false)}
                    data-testid="plant-ground-override-done"
                  >
                    DONE
                  </button>
                  <button
                    type="button"
                    className="plant-ground-override-btn plant-ground-override-btn--ghost"
                    onClick={() => {
                      onResetToSurvey?.();
                      setOverrideMode(false);
                    }}
                    data-testid="plant-ground-reset-survey"
                  >
                    RESET TO SURVEY
                  </button>
                </>
              )}
            </div>
            ) : null}
          </div>

          {readout.showCoverageNote ? (
            <div className="plant-ground-note" data-testid="plant-ground-coverage-note">
              <div className="plant-ground-note__title">NO SOIL SURVEY HERE YET</div>
              <p className="plant-ground-note__body">{readout.coverageNote}</p>
            </div>
          ) : null}

          <div className="plant-ground-next">
            <div className="plant-ground-next__title">WHAT HAPPENS NEXT</div>
            <div className="plant-ground-next__list">
              {GROUND_NEXT_UP.map((item) => (
                <div key={item.num} className="plant-ground-next__row">
                  <span className="plant-ground-next__num">{item.num}</span>
                  <span className="plant-ground-next__text">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
